import { Component } from '@theme/component';

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 3;
export const ZOOM_STEP = 0.5;
export const DOUBLE_TAP_ZOOM = 2;

const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 48;
const DRAG_THRESHOLD = 5;
const KEYBOARD_PAN_STEP = 48;

/**
 * Clamp a requested scale to the supported, source-conscious range.
 *
 * @param {number} value
 * @returns {number}
 */
export function clampZoom(value) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
}

/**
 * Calculate the visible dimensions of an object-fit: contain image.
 *
 * @param {number} containerWidth
 * @param {number} containerHeight
 * @param {number} naturalWidth
 * @param {number} naturalHeight
 * @returns {Size}
 */
export function getContainedImageSize(containerWidth, containerHeight, naturalWidth, naturalHeight) {
  if (containerWidth <= 0 || containerHeight <= 0 || naturalWidth <= 0 || naturalHeight <= 0) {
    return { width: Math.max(0, containerWidth), height: Math.max(0, containerHeight) };
  }

  const imageRatio = naturalWidth / naturalHeight;
  const containerRatio = containerWidth / containerHeight;

  if (imageRatio > containerRatio) {
    return { width: containerWidth, height: containerWidth / imageRatio };
  }

  return { width: containerHeight * imageRatio, height: containerHeight };
}

/**
 * Keep every panned image edge attached to its viewport edge.
 *
 * @param {Point} pan
 * @param {number} scale
 * @param {Size} imageSize
 * @param {Size} viewportSize
 * @returns {Point}
 */
export function clampPan(pan, scale, imageSize, viewportSize) {
  if (scale <= MIN_ZOOM) return { x: 0, y: 0 };

  const maximumX = Math.max(0, (imageSize.width * scale - viewportSize.width) / 2);
  const maximumY = Math.max(0, (imageSize.height * scale - viewportSize.height) / 2);

  return {
    x: Math.min(maximumX, Math.max(-maximumX, pan.x)),
    y: Math.min(maximumY, Math.max(-maximumY, pan.y)),
  };
}

/**
 * Change scale while keeping the inspected point stationary in the viewport.
 * The anchor is expressed relative to the viewport centre in CSS pixels.
 *
 * @param {Point} pan
 * @param {number} previousScale
 * @param {number} nextScale
 * @param {Point} anchor
 * @returns {Point}
 */
export function zoomAroundPoint(pan, previousScale, nextScale, anchor) {
  if (nextScale <= MIN_ZOOM) return { x: 0, y: 0 };

  const ratio = nextScale / previousScale;
  return {
    x: anchor.x - (anchor.x - pan.x) * ratio,
    y: anchor.y - (anchor.y - pan.y) * ratio,
  };
}

/**
 * Pointer-first zoom and pan engine for the primary PDP fullscreen image viewer.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} image
 *
 * @extends {Component<Refs>}
 */
export class ProductDetailZoom extends Component {
  requiredRefs = ['image'];

  #scale = MIN_ZOOM;
  /** @type {Point} */
  #pan = { x: 0, y: 0 };
  /** @type {Map<number, PointerPoint>} */
  #pointers = new Map();
  /** @type {PanGesture | PinchGesture | null} */
  #gesture = null;
  /** @type {{ time: number, point: Point } | null} */
  #lastTap = null;
  /** @type {AbortController | null} */
  #controller = null;
  #resizeObserver = new ResizeObserver(() => this.#commit());

  get #image() {
    return this.refs.image;
  }

  get scale() {
    return this.#scale;
  }

  get minScale() {
    return MIN_ZOOM;
  }

  get maxScale() {
    return MAX_ZOOM;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.addEventListener('pointerup', this.#handlePointerUp, { signal });
    this.addEventListener('pointercancel', this.#handlePointerCancel, { signal });
    this.addEventListener('lostpointercapture', this.#handlePointerCancel, { signal });
    this.addEventListener('wheel', this.#handleWheel, { passive: false, signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.addEventListener('dragstart', this.#preventNativeDrag, { signal });

    this.#resizeObserver.observe(this);
    this.#commit();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver.disconnect();
    this.resetZoom();
  }

  /** Zoom one explicit 50% step around the viewport centre. */
  zoomIn() {
    this.#setZoom(this.#scale + ZOOM_STEP, { x: 0, y: 0 });
  }

  /** Zoom out one explicit 50% step around the viewport centre. */
  zoomOut() {
    this.#setZoom(this.#scale - ZOOM_STEP, { x: 0, y: 0 });
  }

  /** Restore the complete image and clear every gesture offset. */
  resetZoom() {
    this.#scale = MIN_ZOOM;
    this.#pan = { x: 0, y: 0 };
    this.#cancelPointers();
    this.#lastTap = null;
    delete this.dataset.dragging;
    this.#commit();
  }

  /**
   * Pan by screen pixels. Used by arrow keys as a non-pointer equivalent.
   *
   * @param {number} x
   * @param {number} y
   */
  panBy(x, y) {
    if (this.#scale <= MIN_ZOOM) return;
    this.#pan = { x: this.#pan.x + x, y: this.#pan.y + y };
    this.#commit();
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (!event.isPrimary && event.pointerType === 'mouse') return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    event.preventDefault();
    if (event.pointerType !== 'touch') this.focus({ preventScroll: true });

    this.#pointers.set(event.pointerId, pointerPoint(event));
    try {
      this.setPointerCapture(event.pointerId);
    } catch {
      // The pointer may have been canceled between dispatch and capture.
    }

    if (this.#pointers.size >= 2) {
      this.#startPinch();
    } else {
      this.#startPan(event.pointerId);
    }
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    this.#pointers.set(event.pointerId, pointerPoint(event));

    if (this.#pointers.size >= 2) {
      event.preventDefault();
      this.#updatePinch();
      return;
    }

    const gesture = this.#gesture;
    if (!gesture || gesture.type !== 'pan' || gesture.pointerId !== event.pointerId) return;

    const current = this.#pointers.get(event.pointerId);
    if (!current) return;

    const deltaX = current.x - gesture.startPoint.x;
    const deltaY = current.y - gesture.startPoint.y;
    if (Math.hypot(deltaX, deltaY) >= DRAG_THRESHOLD) gesture.moved = true;

    if (this.#scale <= MIN_ZOOM || !gesture.moved) return;

    event.preventDefault();
    this.dataset.dragging = 'true';
    this.#pan = {
      x: gesture.startPan.x + deltaX,
      y: gesture.startPan.y + deltaY,
    };
    this.#commit();
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    const point = this.#pointers.get(event.pointerId);
    if (!point) return;

    const gesture = this.#gesture;
    const wasPinch = gesture?.type === 'pinch';
    const wasMoved = gesture?.type === 'pan' ? gesture.moved : true;

    this.#pointers.delete(event.pointerId);
    this.#releasePointer(event.pointerId);
    delete this.dataset.dragging;

    if (wasPinch) {
      const remaining = this.#pointers.keys().next().value;
      if (typeof remaining === 'number') this.#startPan(remaining, true);
      else this.#gesture = null;
      return;
    }

    this.#gesture = null;
    if (!wasMoved) this.#handleTap(point, event.pointerType);
  };

  /** @param {PointerEvent} event */
  #handlePointerCancel = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    this.#pointers.delete(event.pointerId);
    this.#releasePointer(event.pointerId);
    delete this.dataset.dragging;

    const remaining = this.#pointers.keys().next().value;
    if (typeof remaining === 'number') this.#startPan(remaining, true);
    else this.#gesture = null;
  };

  /** @param {WheelEvent} event */
  #handleWheel = (event) => {
    if (!event.ctrlKey && !event.metaKey) return;

    event.preventDefault();
    const factor = Math.exp(-event.deltaY * 0.0025);
    this.#setZoom(this.#scale * factor, this.#pointFromClient(event.clientX, event.clientY));
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    switch (event.key) {
      case '+':
      case '=':
        event.preventDefault();
        this.zoomIn();
        break;
      case '-':
        event.preventDefault();
        this.zoomOut();
        break;
      case '0':
      case 'Home':
        event.preventDefault();
        this.resetZoom();
        break;
      case 'ArrowLeft':
        if (this.#scale <= MIN_ZOOM) return;
        event.preventDefault();
        this.panBy(KEYBOARD_PAN_STEP, 0);
        break;
      case 'ArrowRight':
        if (this.#scale <= MIN_ZOOM) return;
        event.preventDefault();
        this.panBy(-KEYBOARD_PAN_STEP, 0);
        break;
      case 'ArrowUp':
        if (this.#scale <= MIN_ZOOM) return;
        event.preventDefault();
        this.panBy(0, KEYBOARD_PAN_STEP);
        break;
      case 'ArrowDown':
        if (this.#scale <= MIN_ZOOM) return;
        event.preventDefault();
        this.panBy(0, -KEYBOARD_PAN_STEP);
        break;
    }
  };

  /** @param {DragEvent} event */
  #preventNativeDrag = (event) => event.preventDefault();

  /** @param {number} pointerId @param {boolean} [moved] */
  #startPan(pointerId, moved = false) {
    const point = this.#pointers.get(pointerId);
    if (!point) return;

    this.#gesture = {
      type: 'pan',
      pointerId,
      startPoint: { x: point.x, y: point.y },
      startPan: { x: this.#pan.x, y: this.#pan.y },
      moved,
    };
  }

  #startPinch() {
    const points = Array.from(this.#pointers.entries()).slice(0, 2);
    const first = points[0];
    const second = points[1];
    if (!first || !second) return;

    const distance = pointDistance(first[1], second[1]);
    if (distance <= 0) return;

    this.#gesture = {
      type: 'pinch',
      pointerIds: [first[0], second[0]],
      startDistance: distance,
      startScale: this.#scale,
      startPan: { x: this.#pan.x, y: this.#pan.y },
      startMidpoint: this.#pointFromClient(
        (first[1].x + second[1].x) / 2,
        (first[1].y + second[1].y) / 2
      ),
    };
  }

  #updatePinch() {
    const gesture = this.#gesture;
    if (!gesture || gesture.type !== 'pinch') return;

    const first = this.#pointers.get(gesture.pointerIds[0]);
    const second = this.#pointers.get(gesture.pointerIds[1]);
    if (!first || !second || gesture.startDistance <= 0) return;

    const nextScale = clampZoom(
      gesture.startScale * (pointDistance(first, second) / gesture.startDistance)
    );
    const currentMidpoint = this.#pointFromClient((first.x + second.x) / 2, (first.y + second.y) / 2);
    const ratio = nextScale / gesture.startScale;

    this.#scale = nextScale;
    this.#pan = nextScale <= MIN_ZOOM
      ? { x: 0, y: 0 }
      : {
          x: currentMidpoint.x - (gesture.startMidpoint.x - gesture.startPan.x) * ratio,
          y: currentMidpoint.y - (gesture.startMidpoint.y - gesture.startPan.y) * ratio,
        };
    this.#commit();
  }

  /** @param {Point} point @param {string} pointerType */
  #handleTap(point, pointerType) {
    if (pointerType !== 'touch') {
      this.#toggleZoomAt(point);
      return;
    }

    const now = performance.now();
    const previous = this.#lastTap;
    this.#lastTap = { time: now, point: { x: point.x, y: point.y } };

    if (!previous || now - previous.time > DOUBLE_TAP_DELAY) return;
    if (pointDistance(point, previous.point) > DOUBLE_TAP_DISTANCE) return;

    this.#lastTap = null;
    this.#handleDoubleTap(point);
  }

  /** @param {Point} point */
  #handleDoubleTap(point) {
    this.#toggleZoomAt(point);
  }

  /** @param {Point} point */
  #toggleZoomAt(point) {
    const target = this.#scale <= MIN_ZOOM ? DOUBLE_TAP_ZOOM : MIN_ZOOM;
    this.#setZoom(target, this.#pointFromClient(point.x, point.y));
  }

  /** @param {number} nextScale @param {Point} anchor */
  #setZoom(nextScale, anchor) {
    const constrainedScale = clampZoom(nextScale);
    const previousScale = this.#scale;
    if (Math.abs(constrainedScale - previousScale) < 0.001) return;

    this.#scale = constrainedScale;
    this.#pan = zoomAroundPoint(this.#pan, previousScale, constrainedScale, anchor);
    this.#commit();
  }

  /** @param {number} clientX @param {number} clientY @returns {Point} */
  #pointFromClient(clientX, clientY) {
    const rect = this.getBoundingClientRect();
    return {
      x: clientX - (rect.left + rect.width / 2),
      y: clientY - (rect.top + rect.height / 2),
    };
  }

  #commit() {
    const rect = this.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0 && this.#image) {
      const imageSize = getContainedImageSize(
        rect.width,
        rect.height,
        this.#image.naturalWidth || rect.width,
        this.#image.naturalHeight || rect.height
      );
      this.#pan = clampPan(this.#pan, this.#scale, imageSize, { width: rect.width, height: rect.height });
    }

    this.style.setProperty('--detail-zoom-scale', this.#scale.toString());
    this.style.setProperty('--detail-zoom-x', `${this.#pan.x}px`);
    this.style.setProperty('--detail-zoom-y', `${this.#pan.y}px`);
    this.dataset.zoomed = String(this.#scale > MIN_ZOOM);

    this.dispatchEvent(
      new CustomEvent('product-detail-zoom:change', {
        bubbles: true,
        detail: { scale: this.#scale, min: MIN_ZOOM, max: MAX_ZOOM },
      })
    );
  }

  #cancelPointers() {
    const pointerIds = Array.from(this.#pointers.keys());
    this.#pointers.clear();
    this.#gesture = null;

    for (const pointerId of pointerIds) this.#releasePointer(pointerId);
    delete this.dataset.dragging;
  }

  /** @param {number} pointerId */
  #releasePointer(pointerId) {
    try {
      if (this.hasPointerCapture(pointerId)) this.releasePointerCapture(pointerId);
    } catch {
      // Capture can already be gone after a browser-level cancellation.
    }
  }
}

/** @param {PointerEvent} event @returns {PointerPoint} */
function pointerPoint(event) {
  return { x: event.clientX, y: event.clientY, pointerType: event.pointerType };
}

/** @param {Point} first @param {Point} second */
function pointDistance(first, second) {
  return Math.hypot(first.x - second.x, first.y - second.y);
}

if (!customElements.get('product-detail-zoom')) {
  customElements.define('product-detail-zoom', ProductDetailZoom);
}

/** @typedef {{ x: number, y: number }} Point */
/** @typedef {{ width: number, height: number }} Size */
/** @typedef {{ x: number, y: number, pointerType: string }} PointerPoint */
/** @typedef {{ type: 'pan', pointerId: number, startPoint: Point, startPan: Point, moved: boolean }} PanGesture */
/** @typedef {{ type: 'pinch', pointerIds: [number, number], startDistance: number, startScale: number, startPan: Point, startMidpoint: Point }} PinchGesture */
